import {DownloadOutlined, PrinterOutlined} from '@ant-design/icons';
import {Button, Modal} from 'antd';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import toast from 'react-hot-toast';
// eslint-disable-next-line import/no-unresolved
import {useReactToPrint} from 'react-to-print';

import logo from '../../../../../Assets/Logo3.png';

const PRINT_STYLE = `
  @page {
    size: A4 portrait;
    margin: 0;
  }

  @media print {
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      width: 210mm !important;
      background: #fff !important;
    }

    body * {
      visibility: hidden !important;
    }

    .invoice-print-area,
    .invoice-print-area * {
      visibility: visible !important;
    }

    .invoice-print-area {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 210mm !important;
      min-height: 297mm !important;
      margin: 0 !important;
      padding: 5mm !important;
      background: #fff !important;
      box-sizing: border-box !important;
    }

    .invoice-print-area table {
      page-break-inside: avoid;
    }

    .invoice-print-area tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }

    .invoice-print-area img {
      max-width: 100%;
    }

    .ant-modal,
    .ant-modal-wrap,
    .ant-modal-mask,
    .ant-modal-content,
    .ant-modal-header,
    .ant-modal-footer {
      visibility: hidden !important;
    }
  }
`;

/* ── company constants — update to match your business ─────────────────────── */
const COMPANY = {
  name:    'SAMADHAN INDUSTRIES',
  address: 'Plot No.- F-1, MIDC, ISHWARPUR.',
  city:    'Tal.- Walwa, Dist.- Sangli-415409',
  state:   'State Code: MH (27)',
  email:   'samadhanindustriesgroup89@gmail.com',
  phone:   '8412912712 / 8485824712',
  gstin:   'NA',
  pan:     'ABRPG7669K',
};

const BANK = {
  name:   'BANK OF MAHARASHTRA',
  branch: 'URUN ISLAMPUR',
  ac:     '60293437719',
  ifsc:   'MAHB0000466',
  acType: 'CURRENT A/C',
};

/* ── helpers ────────────────────────────────────────────────────────────────── */
const toWords = n => {
  if (n === 0) {return 'ZERO RUPEES ONLY';}
  const ones  = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
    'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
    'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens  = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  const toH   = n => n >= 100
    ? `${ones[Math.floor(n / 100)]} HUNDRED${n % 100 ? ` ${toH(n % 100)}` : ''}`
    : n >= 20
      ? `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`
      : ones[n];
  const int   = Math.floor(n);
  const lakh  = Math.floor(int / 100000);
  const thou  = Math.floor((int % 100000) / 1000);
  const rem   = int % 1000;
  const parts = [];
  if (lakh) {parts.push(`${toH(lakh)} LAKH`);}
  if (thou) {parts.push(`${toH(thou)} THOUSAND`);}
  if (rem)  {parts.push(toH(rem));}
  return `${parts.join(' ')} RUPEES ONLY`;
};

/* convert a bill API response into the invoiceData shape used by InvoiceTemplate */
const buildInvoiceData = bill => {
  const rows = (bill.items ?? []).map(item => ({
    productLabel: item.productName ?? '',
    hsnCode:      item.hsnCode     ?? '',
    kgsBag:       item.bagsWeight  ?? item.bags ?? 0,
    bags:         item.bags        ?? 0,
    qty:          item.quantity    ?? 0,
    cgst:         item.cgst        ?? 0,
    igst:         item.igst        ?? 0,
    rate:         item.unitPrice   ?? 0,
    total:        item.totalPrice  ?? 0,
  }));

  // const totalBags  = rows.reduce((s, r) => s + Number(r.bags  ?? 0), 0);
  // const totalQty   = rows.reduce((s, r) => s + Number(r.qty   ?? 0), 0);
  // const totalCgst  = rows.reduce((s, r) => s + Number(r.cgst  ?? 0), 0);
  // const totalIgst  = rows.reduce((s, r) => s + Number(r.igst  ?? 0), 0);
  // const subTotal   = rows.reduce((s, r) => s + Number(r.total ?? 0), 0);
  // const totalAmt   = subTotal * totalIgst;

  const totalBags = rows.reduce((s, r) => s + Number(r.bags ?? 0), 0);
  const totalQty = rows.reduce((s, r) => s + Number(r.qty ?? 0), 0);
  const totalCgst = rows.reduce((s, r) => s + Number(r.cgst ?? 0), 0);
  const totalIgst = rows.reduce((s, r) => s + Number(r.igst ?? 0), 0);
  const subTotal = rows.reduce((s, r) => s + Number(r.total ?? 0), 0);
  const igstAmount = (subTotal * totalIgst) / 100;
  // Final amount including IGST
  const totalAmt = subTotal + igstAmount;
  
  const transport = bill.transportDetails ?? {};

  return {
    invoiceNo:    bill.billNumber ?? '',
    invoiceDate:  dayjs(bill.billDate ?? bill.createdAt).format('DD-MMM-YYYY'),
    ldNo:         transport.challanNumber ? `L.D. No: ${transport.challanNumber}` : '',
    paidAmount:   bill.paidAmount    ?? 0,
    pendingAmount: bill.pendingAmount ?? 0,

    billing: {
      customerName: bill.customerName    ?? '',
      businessName: bill.businessName    ?? '',
      address:      bill.customerAddress ?? '',
      district:     bill.shippingAddress?.district ?? '',
      cellNo:       bill.customerMobile  ?? '',
      stateCode:    '',
      gstin:        bill.gstIn           ?? 'N/A',
    },
    shipping: {
      customerName: bill.customerName ?? '',
      address:      bill.shippingAddress?.address  ?? bill.customerAddress ?? '',
      street:       bill.shippingAddress?.street   ?? '',
      city:         bill.shippingAddress?.city     ?? '',
      district:     bill.shippingAddress?.district ?? '',
      pinCode:      bill.shippingAddress?.pinCode  ?? '',
    },

    terms: {
      customerOrderNo:  'TELEPHONIC',
      termsOfPayment:   '7 DAYS',
      deliveryNote:     bill.billNumber ?? '',
      dispatchedThrough: transport.transportName ?? 'BY ROAD',
      destination:      transport.destination    ?? '',
      motorVehicleNo:   transport.vehicleNumber  ?? '',
      freight:          transport.freightPaid === true ? 'TO PAY' : 'PAID',
      modeOfPayment:    bill.paymentMethod ?? '',
    },

    rows,
    totals: {
      bags:  totalBags,
      qty:   totalQty,
      cgst:  totalCgst,
      igst:  totalIgst,
      total: totalAmt,
    },
    amountInWords: toWords(Math.round(totalAmt)),
  };
};

/* ── InvoiceTemplate — same layout as GenerateBillPdf ──────────────────────── */
export const InvoiceTemplate = ({invoiceData, printRef}) => {
  const {
    invoiceNo, invoiceDate, ldNo, paidAmount, pendingAmount,
    billing, shipping, terms,
    rows, totals, amountInWords,
  } = invoiceData;

  const grossValue   = totals.total;
  const taxAmount    = (totals.cgst || 0) + (totals.igst || 0);
  const invoiceValue = grossValue;
  const displayRows  = Array.from({length: Math.max(rows.length, 5)});

  return (
    <div
      ref={printRef}
      className={'invoice-print-area font-[\'Times_New_Roman\',Times,serif] text-[11px] text-black bg-white p-[8mm_10mm] w-full box-border'}
    >
      {/* header row */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '4px'}}>
        <tbody>
          <tr>
            <td style={{verticalAlign: 'middle', width: '33%', textAlign: 'center'}}>
              <p style={{textAlign: 'center', fontSize: '9px', margin: '0 0 2px', fontWeight: 'bold'}}>
                SUBJECT TO ISHWARPUR JURISDICTION
              </p>
              <img
                src={logo}
                alt={'Samadhan Industries'}
                style={{height: '56px', objectFit: 'contain', display: 'block', margin: '0 auto'}}
                onError={e => { e.target.style.display = 'none'; }}
              />
            </td>
            <td style={{verticalAlign: 'middle', width: '34%', textAlign: 'center'}}>
              <p style={{fontSize: '22px', fontWeight: 'bold', margin: 0, letterSpacing: '4px'}}>
                TAX INVOICE
              </p>
            </td>
            <td style={{verticalAlign: 'middle', width: '33%', textAlign: 'right'}}>
              <p style={{margin: '0 0 2px', fontWeight: 'bold', fontSize: '11px'}}>
                INVOICE NO: {invoiceNo}
              </p>
              <p style={{margin: 0, fontWeight: 'bold', fontSize: '11px'}}>
                INVOICE DATE: {invoiceDate}
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <p style={{textAlign: 'center', fontSize: '8px', margin: '0 0 4px'}}>
        ORIGINAL FOR RECIPIENT, DUPLICATE FOR TRANSPORTER, TRIPLICATE FOR SUPPLIER
        (ISSUE OF INVOICE UNDER RULE 7 OF CENTRAL GST RULES IN SECTION - 31 - 2017)
      </p>

      {/* company + bank */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '16px'}}>
        <tbody>
          <tr>
            <td style={{verticalAlign: 'top', width: '60%', paddingRight: '8px'}}>
              <p style={{margin: '0', fontSize: '11px'}}><b>Company Name :-</b> <b>{COMPANY.name}</b></p>
              <p style={{margin: '0', fontSize: '11px'}}><b>Address:-</b></p>
              <p style={{margin: '0', fontSize: '11px'}}>{COMPANY.address}</p>
              <p style={{margin: '0', fontSize: '11px'}}>{COMPANY.city}</p>
              <p style={{margin: '0', fontSize: '11px'}}>{COMPANY.state}</p>
              <p style={{margin: '0', fontSize: '11px'}}>Email- {COMPANY.email}</p>
              <p style={{margin: '0', fontSize: '11px'}}>Factory/off.- {COMPANY.phone}</p>
              <p style={{margin: '0', fontSize: '11px'}}>
                <b>GSTIN:</b> {COMPANY.gstin}&nbsp;&nbsp;<b>PAN NO.-</b> {COMPANY.pan}
              </p>
            </td>
            <td style={{verticalAlign: 'top', width: '40%', border: '1px solid black', padding: '4px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                  <tr>
                    <td style={{padding: '2px 4px', fontWeight: 'bold', fontSize: '11px'}}>BANKING DETAILS :</td>
                  </tr>
                  {[
                    ['BANK',     BANK.name],
                    ['BR',       BANK.branch],
                    ['A/C',      BANK.ac],
                    ['IFSC',     BANK.ifsc],
                    ['A/C TYPE', BANK.acType],
                  ].map(([label, val]) => (
                    <tr key={label}>
                      <td style={{fontSize: '10px', padding: '1px 4px'}}>{label} : {val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* billing + shipping */}
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid black', marginBottom: '4px'}}>
        <thead>
          <tr>
            <th style={{borderBottom: '1px solid black', padding: '4px 6px', textAlign: 'center', width: '50%', background: '#e9edf4', fontWeight: 'bold', fontSize: '11px'}}>
              Billing Address
            </th>
            <th style={{borderBottom: '1px solid black', borderLeft: '1px solid black', padding: '4px 6px', textAlign: 'center', width: '50%', background: '#e9edf4', fontWeight: 'bold', fontSize: '11px'}}>
              Shipping Address
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{padding: '6px 8px', verticalAlign: 'top', fontSize: '11px'}}>
              <p style={{margin: 0}}><b>Customer Name :-</b> {(billing?.customerName || '').toUpperCase()}</p>
              {billing?.businessName && <p style={{margin: 0}}><b>Business :-</b> {billing.businessName.toUpperCase()}</p>}
              <p style={{margin: 0}}><b>Address:-</b> {billing?.address || ''}</p>
              {billing?.district && <p style={{margin: 0}}>DIST.- {billing.district.toUpperCase()}</p>}
              <p style={{margin: 0}}>
                {billing?.cellNo && <><b>CELL NO:</b> {billing.cellNo}</>}
                {billing?.stateCode && <>&nbsp;&nbsp;<b>Code-</b> ({billing.stateCode})</>}
              </p>
              <p style={{margin: 0}}><b>GSTIN :-</b> {billing?.gstin || 'N/A'}</p>
            </td>
            <td style={{padding: '6px 8px', verticalAlign: 'top', fontSize: '11px', borderLeft: '1px solid black'}}>
              <p style={{margin: 0}}><b>Customer Name:</b> {shipping?.customerName || 'AS SAME'}</p>
              <p style={{margin: 0}}><b>Address:</b> {shipping?.address || 'AS SAME'}</p>
              {shipping?.street   && <p style={{margin: 0}}><b>Street:</b> {shipping.street}</p>}
              {shipping?.city     && <p style={{margin: 0}}><b>City:</b> {shipping.city.toUpperCase()}</p>}
              {shipping?.district && <p style={{margin: 0}}><b>District:</b> {shipping.district.toUpperCase()}</p>}
              {shipping?.pinCode  && <p style={{margin: 0}}><b>Pin Code:</b> {shipping.pinCode}</p>}
            </td>
          </tr>
        </tbody>
      </table>

      {/* items table */}
      <table style={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: '4px', border: '1px solid black'}}>
        <colgroup>
          <col style={{width: '4%'}}  />
          <col style={{width: '32%'}} />
          <col style={{width: '9%'}}  />
          <col style={{width: '6%'}}  />
          <col style={{width: '5%'}}  />
          <col style={{width: '5%'}}  />
          <col style={{width: '7%'}}  />
          <col style={{width: '6%'}}  />
          <col style={{width: '12%'}} />
          <col style={{width: '14%'}} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={10} style={{border: '1px solid black', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px', background: '#e9edf4'}}>
              PRODUCTS SUPPLIED
            </th>
          </tr>
          <tr>
            {['SR\nNO', 'PRODUCT', 'HSN\nCODE', 'KGS\nBAG', 'BAGS', 'QTY', 'C/\nSGST', 'IGST', 'PRICE/\nUNIT', 'TOTAL'].map(label => (
              <th key={label} style={{borderBottom: '1px solid black', borderLeft: '1px solid black', padding: '4px 2px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px', background: '#e9edf4', whiteSpace: 'pre-line', height: '36px'}}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((_, idx) => {
            const r = rows[idx];
            const tdStyle = {borderBottom: '1px solid black', borderLeft: '1px solid black', height: '28px', padding: '0 4px', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', fontSize: '11px'};
            return (
              <tr key={idx} style={{height: '28px'}}>
                <td style={tdStyle}>{idx + 1}</td>
                <td style={{...tdStyle, textAlign: 'left', paddingLeft: '6px', fontWeight: r ? 'bold' : 'normal'}}>{r?.productLabel || ''}</td>
                <td style={tdStyle}>{r?.hsnCode || (r ? '#N/A' : '')}</td>
                <td style={tdStyle}>{r?.kgsBag !== undefined ? r.kgsBag : (r ? '0' : '')}</td>
                <td style={tdStyle}>{r?.bags !== undefined ? r.bags : (r ? '0' : '')}</td>
                <td style={tdStyle}>{r?.qty !== undefined ? r.qty : (r ? '0' : '')}</td>
                <td style={tdStyle}>{r?.cgst !== undefined ? r.cgst : (r ? '0' : '')}</td>
                <td style={tdStyle}>{r?.igst !== undefined ? r.igst : (r ? '0' : '')}</td>
                <td style={tdStyle}>{r?.rate !== undefined ? Number(r.rate).toLocaleString('en-IN') : (r ? '0' : '')}</td>
                <td style={{...tdStyle, fontWeight: r ? 'bold' : 'normal'}}>{r?.total > 0 ? Number(r.total).toLocaleString('en-IN') : (r ? '0' : '')}</td>
              </tr>
            );
          })}
          {/* totals row */}
          <tr style={{height: '28px', background: '#e9edf4'}}>
            {[['TOTAL', 4], [totals.bags || 0, 1], [totals.qty || 0, 1], [totals.cgst || 0, 1], [totals.igst || 0, 1], ['', 1], [Number(totals.total).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}), 1]].map(([content, colSpan], i) => (
              <td key={i} colSpan={colSpan} style={{border: '1px solid black', height: '28px', padding: '0 4px', verticalAlign: 'middle', textAlign: 'center', fontWeight: 'bold', fontSize: '11px', background: '#e9edf4'}}>
                {content}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* amount in words */}
      <div style={{border: '1px solid black', padding: '4px 8px', marginBottom: '4px'}}>
        <span style={{fontWeight: 'bold', fontSize: '11px'}}>Total Amount in Words: </span>
        <span style={{fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase'}}>{amountInWords}</span>
      </div>

      {/* invoice terms + value summary */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '4px'}}>
        <tbody>
          <tr>
            {/* terms */}
            <td style={{verticalAlign: 'top', width: '65%', paddingRight: '8px'}}>
              <table style={{width: '100%', border: '1px solid black', borderCollapse: 'collapse'}}>
                <tbody>
                  <tr>
                    <td colSpan={2} style={{border: '1px solid black', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold', background: '#e9edf4', fontSize: '11px'}}>
                      Invoice Terms
                    </td>
                  </tr>
                  {[
                    [`1. CUSTOMER ORDER NO.- ${terms?.customerOrderNo || 'TELEPHONIC'}`, `INVOICE DATE: ${invoiceDate}`],
                    [`2. TERMS OF PAYMENT:- ${terms?.termsOfPayment || '7 DAYS'}`,      ldNo],
                    ['3. INTEREST : @ 18% P.A. CHARGED IF NOT PAID ON/BEFORE DUE DATE (21 DAYS)', null],
                    [`4. DELIVERY NOTE:- ${terms?.deliveryNote || invoiceNo}`,           `INVOICE DATE: ${invoiceDate}`],
                    [`5. DISPATCHED THROUGH:- ${(terms?.dispatchedThrough || 'BY ROAD').toUpperCase()}`, `DESTINATION-: ${(terms?.destination || '').toUpperCase()}`],
                    [`7. MOTOR VEHICLE NO :- ${(terms?.motorVehicleNo || '').toUpperCase()}`,            'TRANSPORT - LNT'],
                    [`8. FREIGHT:- ${(terms?.freight || 'TO PAY').toUpperCase()}`,       `MODE OF PAYMENT- ${(terms?.modeOfPayment || '').toUpperCase()}`],
                  ].map(([left, right], i) => (
                    <tr key={i}>
                      <td style={{borderBottom: '1px solid black', borderRight: right ? '1px solid black' : 'none', padding: '4px 6px', fontSize: '11px', colSpan: right ? 1 : 2}}>{left}</td>
                      {right !== null && right !== undefined && (
                        <td style={{borderBottom: '1px solid black', padding: '4px 6px', fontSize: '11px', textAlign: 'center'}}>{right}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>

            {/* amount summary */}
            <td style={{verticalAlign: 'top', width: '35%'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid black'}}>
                <tbody>
                  {[
                    {label: 'Gross Value',    value: Number(grossValue).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}),    bg: '',           color: ''},
                    {label: 'Tax (Exempted)', value: taxAmount > 0 ? Number(taxAmount).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00', bg: '', color: ''},
                    {label: 'Invoice Value',  value: Number(invoiceValue).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}),   bg: '',           color: ''},
                    {label: 'Paid Amount',    value: Number(paidAmount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}), bg: '#f0fff0',   color: '#006600'},
                    {label: 'Total Payable',  value: Number(pendingAmount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
                      bg: (pendingAmount || 0) > 0 ? '#fff0f0' : '#f0fff0',
                      color: (pendingAmount || 0) > 0 ? '#cc0000' : '#006600'},
                  ].map(({label, value, bg, color}) => (
                    <tr key={label}>
                      <td style={{borderBottom: '1px solid black', padding: '4px 6px', fontWeight: 'bold', textAlign: 'left', fontSize: '11px', background: bg, color}}>{label}</td>
                      <td style={{borderBottom: '1px solid black', borderLeft: '1px solid black', padding: '4px 6px', fontWeight: 'bold', textAlign: 'right', fontSize: '11px', background: bg, color}}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* signature */}
      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '8px'}}>
        <div style={{textAlign: 'right'}}>
          <p style={{margin: 0, fontWeight: 'bold', fontSize: '11px'}}>For, {COMPANY.name}</p>
          <p style={{marginTop: '40px', marginBottom: 0, fontStyle: 'italic', fontSize: '10px'}}>Authorised Signatory</p>
        </div>
      </div>
    </div>
  );
};

InvoiceTemplate.propTypes = {
  invoiceData: PropTypes.object.isRequired,
  printRef:    PropTypes.object.isRequired,
};

/* ── BillDownload modal ─────────────────────────────────────────────────────── */
export const BillDownload = ({open, bill, onClose}) => {
  const printRef                      = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Invoice-${bill?.billNumber || 'Bill'}`,
    pageStyle: PRINT_STYLE,
  });

  if (!bill) {return null;}

  const invoiceData = buildInvoiceData(bill);

  const handleDownload = async () => {
    if (!printRef.current) {return;}
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin:      [5, 5, 5, 5],
          filename:    `${invoiceData.invoiceNo.replace(/\//g, '-')}.pdf`,
          image:       {type: 'jpeg', quality: 0.98},
          html2canvas: {
            scale:   2,
            useCORS: true,
            logging: false,
            onclone: doc => {
              doc.querySelectorAll('td, th').forEach(el => {
                if (el.style.verticalAlign !== 'top') {el.style.verticalAlign = 'middle';}
                el.style.lineHeight = '1.2';
              });
            },
          },
          jsPDF: {unit: 'mm', format: 'a4', orientation: 'portrait'},
        })
        .from(printRef.current)
        .save();
      toast.success('Invoice PDF downloaded!');
      onClose();
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={1100}
      title={(
        <div className={'flex items-center gap-2'}>
          <DownloadOutlined className={'text-blue-600'} />
          <span className={'font-bold text-gray-800 text-base'}>
            Invoice Preview —{' '}
            <span className={'font-mono text-blue-600'}>{bill.billNumber}</span>
          </span>
        </div>
      )}
      styles={{body: {maxHeight: '80vh', overflowY: 'auto', padding: '16px 20px'}}}
      footer={(
        <div className={'flex justify-end gap-3 py-1.5'}>
          <Button className={'btn-secondary'} onClick={onClose}>Close</Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={() => handlePrint && handlePrint()}
          >
            Print Invoice
          </Button>
          <Button
            className={'btn-primary'}
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </div>
      )}
    >
      <div className={'border border-slate-200 rounded-lg overflow-hidden shadow-sm'}>
        <InvoiceTemplate invoiceData={invoiceData} printRef={printRef} />
      </div>
    </Modal>
  );
};

BillDownload.propTypes = {
  open:    PropTypes.bool.isRequired,
  bill:    PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default BillDownload;
